import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material/styles";
import "./index.css";
import { SideDrawer } from "./Drawer";
import { Avatar, FormControl, Typography } from "@mui/material";
import TextInput from "../../../components/TextInput";
import { isMobileBrowser } from "../../../utils/checkDeviceType";
import { drawerWidth } from "../../../constants";
import BoLogo from "../../../assets/images/bo-logo.svg";
import { useNavigate } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import UserNav from "./UserNav";
import { setUser } from "../../../rtkSlices/metaDataSlice";

interface Props {
  window?: () => Window;
  title?: string;
  id?: number;
}

export default function Sidebar(props: Props) {
  const { window } = props;
  const theme = useTheme();
  const bgdefault = theme.palette.background.default;
  const metaData = useSelector((state: any) => state.metaData, shallowEqual);
  const dispatch = useDispatch();
  const container =
    window !== undefined ? () => window().document.body : undefined;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const navigate = useNavigate();
  const { firstName, lastName, given_name, family_name } = metaData.userInfo;
  const username = firstName
    ? `${firstName} ${lastName}`
    : given_name
    ? `${given_name} ${family_name}`
    : "";
  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };
  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  function stringAvatar(name: string) {
    return {
      sx: {
        bgcolor: "#2a945f",
      },
      children:
        name.split(" ") && name.split(" ")[0]
          ? `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`
          : ``,
    };
  }

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  React.useEffect(() => {
    if (isMobileBrowser()) {
      // true for mobile device
      const toggle: HTMLElement | null =
        document.getElementById("theme-toggle");
      if (toggle && !mobileOpen) {
        toggle.style.display = "none";
      }
      if (toggle && mobileOpen) {
        setTimeout(() => {
          toggle.style.display = "block";
        }, 150);
      }
    }
  }, [mobileOpen]);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          boxShadow: "none !important",
          background: "none !important",
          display: { sm: "none" },
        }}
      >
        <Toolbar
          sx={{
            backgroundColor: bgdefault,
            boxShadow: "none !important",
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              display: { sm: "none" },
              backgroundColor: "#2a945f",
              padding: "8px",
              borderRadius: "30px",
              marginTop: 1,
              marginLeft: "1px",
            }}
          >
            <MenuIcon />
          </IconButton>
          <div className="new-page-title">
            <Typography fontSize={20}>{props.title}</Typography>
          </div>
          <img
            className="bo-logo"
            src={BoLogo}
            onClick={() => {
              navigate("/user-management");
            }}
          />
        </Toolbar>
      </AppBar>
      {/* <img
        className="bo-logo"
        src={BoLogo}
        onClick={() => navigate("/user-management")}
      /> */}

      {metaData.userInfo.nav ? (
        <UserNav
          name={`${firstName} ${lastName}`}
          role={metaData.userInfo.role}
          profile_picture={metaData.userInfo.profile_picture}
        />
      ) : (
        <Box className="bo-logo" component="div">
          <Avatar
            {...stringAvatar(username)}
            sx={{
              textTransform: "uppercase",
              fontWeight: "600",
              width: "100%",
              height: "100%",
              fontSize: "24px",
              backgroundColor: "#2a945f",
            }}
            src={
              metaData.userInfo.profile_picture
                ? metaData.userInfo.profile_picture
                : ""
            }
            onClick={() => {
              dispatch(setUser({ nav: true }));
            }}
          />
        </Box>
      )}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: {
              xs: "block",
              sm: "none",
            },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              boxShadow: "0px 6px 12px rgba(52, 180, 116, 0.2)",
            },
          }}
        >
          <SideDrawer
            title={props.title}
            id={props.id}
            handleDrawerToggle={handleDrawerToggle}
          />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              boxShadow: "0px 6px 12px rgba(52, 180, 116, 0.2)",
            },
          }}
          open
        >
          <SideDrawer
            title={props.title}
            id={props.id}
            handleDrawerToggle={handleDrawerToggle}
          />
        </Drawer>
      </Box>
    </>
  );
}
